import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { GIAYPHEPHANHNGHE_API, CHUNGCHI_CHUYENMON_API, ACCOUNT_API } from '@app/utils/apiUrl';
import { messageToast, status200 } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import GiayPhepHanhNgheForm from './GiayPhepHanhNgheForm';
import { getUser } from '@app/utils/oidc';

const GiayPhepHanhNgheEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');
    const [accountId, setAccountId] = useState("");
    const [tenDoiTuong, setTenDoiTuong] = useState('');
    const [chucDanhChuyenMon, setChucDanhChuyenMon] = useState('');

    // 1. Lấy thông tin Giấy phép hành nghề (ĐÃ FIX CACHE)
    const itemQuery = useQuery(
        ['giayPhepHanhNghe', itemId],
        () => Get(GIAYPHEPHANHNGHE_API.GET_ITEM, itemId),
        {
            enabled: !!itemId,
            cacheTime: 0, // Xóa cache ngay khi unmount
            staleTime: 0, // Dữ liệu luôn được coi là cũ để bắt buộc fetch lại
            refetchOnMount: true // Luôn fetch lại khi component mount
        }
    );

    useEffect(async () => {
        let mounted = true;
        const user = await getUser();
        if (!user?.profile?.sub) return null;
        const accountId = user.profile.sub;

        if (mounted) {
            setAccountId(accountId);
        }

        const accountResponse = await Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${accountId}`)
        const fetchedTenDoiTuong = accountResponse?.doiTuong?.tenDoiTuong || accountResponse?.data?.doiTuong?.tenDoiTuong || '';
        const fetchedChucDanhChuyenMon = accountResponse?.chucDanhChuyenMon?.tenDoiTuong || accountResponse?.data?.chucDanhChuyenMon?.tenDoiTuong || '';

        if (mounted) {
            setTenDoiTuong(fetchedTenDoiTuong);
            setChucDanhChuyenMon(fetchedChucDanhChuyenMon);
        }

        return () => { mounted = false; };
    }, []);

    const currentData = itemQuery.data || {};

    // 2. Lấy thông tin Chứng chỉ chuyên môn dựa vào accountId (ĐÃ FIX CACHE)
    const cccmQuery = useQuery(
        ['chungChiChuyenMonEdit', accountId],
        () => Get(`${CHUNGCHI_CHUYENMON_API.GET_BY_ACCOUNT_INFO_ID}/${accountId}`),
        {
            enabled: !!accountId,
            cacheTime: 0, // Xóa cache ngay khi unmount
            staleTime: 0, // Dữ liệu luôn được coi là cũ để bắt buộc fetch lại
            refetchOnMount: true // Luôn fetch lại khi component mount
        }
    );

    // Sử dụng chung 1 Mutation nhưng gọi 2 API (Sử dụng Promise.all để chạy song song)
    const editMutation = useMutation(async ({ modelGPHN, modelCCCM, deletedCccmIds }) => {
        const apiCalls = [
            Put(GIAYPHEPHANHNGHE_API.UPDATE_ITEM, itemId, modelGPHN)
        ];

        // CHỈ GỌI API CHỨNG CHỈ NẾU CÓ DỮ LIỆU HOẶC CẦN XÓA
        if (modelCCCM && modelCCCM.length > 0) {
            apiCalls.push(Put(CHUNGCHI_CHUYENMON_API.UPDATE_LIST, null, modelCCCM));
        } else {
            // Nếu không có chứng chỉ để update, vẫn thêm placeholder để đảm bảo index giống lúc check response
            apiCalls.push(Promise.resolve({ status: 200, data: [] }));
        }

        // BỔ SUNG LÔGIC XÓA: Duyệt qua mảng ID bị xóa và push API vào mảng chạy song song
        if (deletedCccmIds && deletedCccmIds.length > 0) {
            deletedCccmIds.forEach(deletedId => {
                const payload = {
                    id: deletedId,
                    fieldName: 'IsDeleted',
                    value: 1
                };
                apiCalls.push(Put(`${CHUNGCHI_CHUYENMON_API.UPDATE_FIELD}`, null, payload));
            });
        }

        // Chạy tất cả các API cùng 1 lúc (Giấy phép, Cập nhật list CCCM, Xóa CCCM)
        const responses = await Promise.all(apiCalls);

        // Trả về 2 response chính để check lỗi ở hàm onSuccess
        return { resGPHN: responses[0], resCCCM: responses[1] };
    });

    const handleEdit = (values) => {
        // Model 1: Dành cho Giấy phép hành nghề
        const modelGPHN = {
            accountId: values.accountId,
            loaiGiayPhep: values.loaiGiayPhep,
            noiCap: values.noiCap,
            soGiayPhep: values.soGiayPhep,
            ngayCap: values.ngayCap ? values.ngayCap : null,
            ngayHetHan: values.ngayHetHan ? values.ngayHetHan : null,
            trangThai: values.trangThai,
            fileMinhChungId: values.fileMinhChungId || null,
            phamViHanhNgheIds: values.phamViHanhNgheIds || []
        };

        // Model 2: Dành cho list Chứng chỉ chuyên môn
        const modelCCCM = (values.chungChiChuyenMons || []).filter(cc => {
            return cc && (cc.loaiChungChi || cc.tenChungChi || cc.ngayCap || cc.fileMinhChungId || cc.fileMinhChungPath || cc.ghiChu);
        }).map(cc => {
            const item = {
                accountId: accountId,
                loaiChungChi: cc.loaiChungChi,
                tenChungChi: cc.tenChungChi,
                ghiChu: cc.ghiChu || null,
                ngayCap: cc.ngayCap ? cc.ngayCap : null,
                fileMinhChungId: cc.fileMinhChungId || null
            };

            if (cc.id) {
                item.id = cc.id;
            }

            return item;
        });

        // Lấy mảng ID cần xóa từ values của Formik
        const deletedCccmIds = values.deletedCccmIds || [];

        editMutation.mutate({ modelGPHN, modelCCCM, deletedCccmIds }, {
            onSuccess: ({ resGPHN, resCCCM }) => {
                if (resGPHN.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else if (resCCCM && resCCCM.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);

                    // Cập nhật lại danh sách ở trang ngoài
                    queryClient.invalidateQueries(['giayPhepHanhNgheProfile']);

                    // Xóa cache của chính bộ hồ sơ vừa sửa để lần sau bấm vào Edit sẽ gọi lại API mới
                    queryClient.invalidateQueries(['giayPhepHanhNghe', itemId]);
                    queryClient.invalidateQueries(['chungChiChuyenMonEdit', accountId]);

                    // Reset queries để dọn dẹp bộ nhớ cache hoàn toàn
                    queryClient.resetQueries(['giayPhepHanhNghe', itemId]);
                    queryClient.resetQueries(['chungChiChuyenMonEdit', accountId]);

                    history.push(URI_INTERNAL.GIAYPHEPHANHNGHE_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    // Đợi cả 2 API load xong
    if (itemQuery.isLoading || cccmQuery.isLoading) {
        return <div className="p-4 text-center">Đang tải dữ liệu hồ sơ....</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy giấy phép hành nghề</div>;
    }

    // Xử lý dữ liệu trả về từ API Chứng chỉ
    const rawCccms = cccmQuery.data?.$values || cccmQuery.data || [];
    const listCccms = Array.isArray(rawCccms) ? rawCccms : (rawCccms ? [rawCccms] : []);

    return (
        <GiayPhepHanhNgheForm
            title="Sửa giấy phép hành nghề"
            submitText="Cập nhật"
            tenDoiTuong={tenDoiTuong}
            chucDanhChuyenMon={chucDanhChuyenMon}
            initialValues={{
                accountId: accountId,
                loaiGiayPhep: currentData.loaiGiayPhep ?? currentData.LoaiGiayPhep ?? '',
                noiCap: currentData.noiCap ?? currentData.NoiCap ?? '',
                soGiayPhep: currentData.soGiayPhep || currentData.SoGiayPhep || '',
                ngayCap: currentData.ngayCap || currentData.NgayCap || '',
                ngayHetHan: currentData.ngayHetHan || currentData.NgayHetHan || '',
                trangThai: currentData.trangThai ?? currentData.TrangThai ?? '',
                phamViHanhNgheIds: currentData.phamViHanhNgheIds || currentData.PhamViHanhNgheIds || [],
                fileMinhChungId: currentData.fileMinhChungId || currentData.FileMinhChungId || '',
                fileMinhChungPath: currentData.fileMinhChungPath || currentData.FileMinhChungPath || '',
                fileMinhChungName: currentData.fileMinhChungName || currentData.FileMinhChungName || '',

                // MAP DỮ LIỆU CHỨNG CHỈ ĐỂ ĐẨY VÀO FORM
                chungChiChuyenMons: listCccms.map(cc => {
                    const path = cc.fileMinhChungPath || cc.FileMinhChungPath || '';
                    return {
                        id: cc.id || '',
                        accountId: accountId,
                        loaiChungChi: cc.loaiChungChi || cc.LoaiChungChi || '',
                        tenChungChi: cc.tenChungChi || cc.TenChungChi || '',
                        ghiChu: cc.ghiChu || cc.GhiChu || '',
                        ngayCap: cc.ngayCap || cc.NgayCap || '',
                        fileMinhChungId: cc.fileMinhChungId || cc.FileMinhChungId || '',
                        fileMinhChungPath: path,
                        // BỔ SUNG: Lấy tên file, nếu API không trả về thì tự động cắt từ đường dẫn Path
                        fileMinhChungName: cc.fileMinhChungName || cc.FileMinhChungName || (path ? path.split('/').pop() : ''),
                        trangThai: cc.trangThai ?? cc.TrangThai ?? null
                    };
                })
            }}
            onSubmit={handleEdit}
            onCancel={() => history.push(URI_INTERNAL.GIAYPHEPHANHNGHE_PATH)}
            isSubmitting={editMutation.isLoading}
        />
    );
};

export default GiayPhepHanhNgheEdit;